import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchThemes,
  uploadDrawing,
  generateVideo,
  fetchTokenPacks,
  createCheckoutSession,
  fetchGenerationStatus,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib";
import { toast } from "sonner";
import { resizeImageIfNeeded } from "../utils/imageResize";
import { Loader2, Sparkles, Star, UploadCloud, Wand2 } from "lucide-react";

import { ThemeCard, UploadDropzone } from "../components/shared";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "../components/ui";
import { cn } from "../components/ui/utils";

const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/heic",
  "image/heif",
];

const DURATION_OPTIONS = [4, 5, 6, 7, 8];
const MIN_DURATION_SECONDS = Math.min(...DURATION_OPTIONS);
const MAX_DURATION_SECONDS = Math.max(...DURATION_OPTIONS);

const THEME_EMOJIS = ["✨", "🐠", "🚀", "🌸", "🦄", "🐉", "🌈", "🪄"];

const Dashboard = () => {
  const { user } = useAuth();

  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [story, setStory] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(8);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  const [tokenPacks, setTokenPacks] = useState([]);
  const [tokenPacksLoading, setTokenPacksLoading] = useState(true);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutNotice, setCheckoutNotice] = useState("");
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [queuedJobId, setQueuedJobId] = useState("");
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [themeQuery, setThemeQuery] = useState("");

  const decoratedThemes = useMemo(
    () =>
      themes.map((theme, index) => ({
        ...theme,
        emoji: THEME_EMOJIS[index % THEME_EMOJIS.length],
      })),
    [themes]
  );

  const selectedThemeData = useMemo(
    () => decoratedThemes.find((theme) => theme.id === selectedTheme),
    [decoratedThemes, selectedTheme]
  );

  const featuredThemes = useMemo(
    () => decoratedThemes.slice(0, 4),
    [decoratedThemes]
  );

const filteredThemes = useMemo(() => {
  if (!themeQuery.trim()) {
    return decoratedThemes;
  }
  const query = themeQuery.toLowerCase();
  return decoratedThemes.filter(
    (theme) =>
      theme.name.toLowerCase().includes(query) ||
      (theme.description || "").toLowerCase().includes(query)
  );
}, [decoratedThemes, themeQuery]);

  const handleThemeSelect = (themeId, shouldCloseDialog = false) => {
    setSelectedTheme(themeId);
    if (shouldCloseDialog) {
      setIsThemeDialogOpen(false);
    }
  };

  useEffect(() => {
    const loadThemes = async () => {
      setThemesLoading(true);
      try {
        const data = await fetchThemes();
        setThemes(data);
        if (data.length) {
          setSelectedTheme(data[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error(
          "We couldn’t load the magic themes. Please check the backend."
        );
      } finally {
        setThemesLoading(false);
      }
    };

    const loadTokenPacks = async () => {
      setTokenPacksLoading(true);
      try {
        const data = await fetchTokenPacks();
        setTokenPacks(data?.packs || []);
        if (data?.packs?.length) {
          setSelectedPackId(data.packs[0].id);
        }
      } catch (err) {
        console.error(err);
        const message =
          err?.response?.data?.detail ||
          err.message ||
          "Failed to load token packs.";
        setCheckoutError(message);
        toast.error(message);
      } finally {
        setTokenPacksLoading(false);
      }
    };

    loadThemes();
    loadTokenPacks();
  }, []);

  const refreshWallet = useCallback(async () => {
    if (!user?.id) {
      setTokenBalance(0);
      return;
    }

    setWalletLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_wallets")
        .select("token_balance")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) {
        throw error;
      }
      setTokenBalance(data?.token_balance ?? 0);
    } catch (err) {
      console.error(err);
      toast.error("Unable to refresh your magic stars right now.");
    } finally {
      setWalletLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshWallet();
  }, [refreshWallet]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    if (status === "success") {
      const message = "Payment received! Your tokens will appear shortly.";
      setCheckoutNotice(message);
      toast.success(message);
      refreshWallet();
    } else if (status === "cancelled") {
      const message = "Checkout was cancelled.";
      setCheckoutNotice(message);
      toast.info(message);
    }
  }, [refreshWallet]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
      }),
    []
  );

  const handleStartCheckout = async (packId) => {
    const targetPackId = packId || selectedPackId;
    if (!targetPackId) {
      const message = "Please select a token pack.";
      setCheckoutError(message);
      toast.error(message);
      return;
    }
    if (!user?.id) {
      const message = "You must be signed in to purchase tokens.";
      setCheckoutError(message);
      toast.error(message);
      return;
    }

    setCheckoutError("");
    setCheckoutLoading(true);

    try {
      const successUrl = `${window.location.origin}/dashboard?checkout=success`;
      const cancelUrl = `${window.location.origin}/dashboard?checkout=cancelled`;
      const response = await createCheckoutSession({
        packId: targetPackId,
        successUrl,
        cancelUrl,
        email: user.email,
      });

      if (response?.checkout_url) {
        setIsTokenDialogOpen(false);
        window.location.href = response.checkout_url;
      } else {
        const message = "Unable to start checkout session.";
        setCheckoutError(message);
        toast.error(message);
      }
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.detail ||
        err.message ||
        "Unable to start checkout. Please try again.";
      setCheckoutError(message);
      toast.error(message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!queuedJobId) return;

    const channel = supabase
      .channel("realtime-generations")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_generations",
          filter: `job_id=eq.${queuedJobId}`,
        },
        async (payload) => {
          console.log("Received realtime update:", payload);
          if (payload.new.status === "completed") {
            try {
              const data = await fetchGenerationStatus(queuedJobId);
              if (data.video_url) {
                setDownloadUrl(data.video_url);
                setQueuedJobId("");
                toast.success("🎉 Your doodle is alive! Check the download below.");
                refreshWallet();
              }
            } catch (err) {
              console.error("Failed to fetch completed video:", err);
            }
          } else if (payload.new.status === "failed") {
            setQueuedJobId("");
            toast.error("Something went wrong while generating your video.");
          }
        }
      )
      .subscribe((status, err) => {
        console.log("Realtime subscription status:", status);
        if (err) {
          console.error("Realtime subscription error:", err);
        }
        if (status === "SUBSCRIBED") {
          console.log("Listening for updates on job:", queuedJobId);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queuedJobId, refreshWallet]);

  const handleFiles = async (files) => {
    const candidate = files?.[0];
    if (!candidate) return;

    if (!ACCEPTED_TYPES.includes(candidate.type)) {
      toast.error("Unsupported file type. Please use PNG, JPEG, or HEIC images.");
      return;
    }

    if (candidate.size > 10 * 1024 * 1024) {
      toast.error("File is too large. Please upload an image under 10MB.");
      return;
    }

    let processed = candidate;
    if (candidate.size > 2 * 1024 * 1024) {
      const toastId = toast.loading("Optimizing image…");
      try {
        processed = await resizeImageIfNeeded(candidate);
      } finally {
        toast.dismiss(toastId);
      }
    }

    setFile(processed);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(processed));
  };

  const handleGenerate = async () => {
    if (!file) {
      toast.error("Please upload a doodle first.");
      return;
    }
    if (!selectedTheme) {
      toast.error("Pick a magic theme before generating.");
      return;
    }
    if (!user?.id) {
      toast.error("You must be signed in to make videos.");
      return;
    }
    if ((tokenBalance || 0) < durationSeconds) {
      toast.error(
        `Not enough magic stars! You need ${durationSeconds} stars for this story.`
      );
      setIsTokenDialogOpen(true);
      return;
    }

    setDownloadUrl("");
    setQueuedJobId("");
    setIsProcessing(true);
    const toastId = toast.loading("Uploading your doodle…");

    try {
      const uploadResponse = await uploadDrawing(file);
      toast.loading("Sprinkling magic…", { id: toastId });

      const generationResponse = await generateVideo({
        image_gcs_uri: uploadResponse.image_gcs_uri,
        theme: selectedTheme,
        story: story || undefined,
        duration_seconds: durationSeconds,
      });

      const immediateUrl = generationResponse.video_url || "";
      setDownloadUrl(immediateUrl);
      setQueuedJobId(generationResponse.job_id || "");
      toast.success(
        immediateUrl
          ? "🎉 Your doodle is alive! Check the download below."
          : "✨ Your doodle is animating! We’ll drop it into My Videos shortly.",
        { id: toastId }
      );
      await refreshWallet();
      setStory("");
      setFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl("");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
        err.message ||
        "Something went wrong while generating your video.",
        { id: toastId }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Sparkles className="h-4 w-4" />
            Magic Studio
          </div>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-foreground">
            Upload your doodle, pick a theme, and sprinkle some magic!
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Snap a photo, choose a story style, and we&apos;ll handle the animation.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end sm:text-right">
          <Dialog open={isTokenDialogOpen} onOpenChange={setIsTokenDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 rounded-full border-border/60 px-3 py-1.5 text-sm font-semibold text-foreground/80"
              >
                <Star className="h-4 w-4 text-primary" />
                Get more stars
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl space-y-6">
              <DialogHeader>
                <DialogTitle>Get more magic stars</DialogTitle>
                <DialogDescription>
                  Choose a star pack to keep creating animated memories.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 sm:grid-cols-2">
                {tokenPacks.map((pack) => {
                  const isSelected = pack.id === selectedPackId;
                  const priceLabel = currencyFormatter.format(
                    (pack.amount_cents || 0) / 100
                  );
                  const tokensNumeric = Number(pack.tokens) || 0;
                  const minVideos = Math.max(
                    1,
                    Math.floor(tokensNumeric / MAX_DURATION_SECONDS)
                  );
                  const maxVideos = Math.max(
                    1,
                    Math.floor(tokensNumeric / MIN_DURATION_SECONDS)
                  );
                  const videoEstimateLabel =
                    minVideos === maxVideos
                      ? `${minVideos} ${minVideos === 1 ? "video" : "videos"}`
                      : `${minVideos}-${maxVideos} videos`;
                  return (
                    <div
                      key={pack.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPackId(pack.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedPackId(pack.id);
                        }
                      }}
                      className={cn(
                        "rounded-xl border border-border/60 bg-white px-4 py-3 text-left transition hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        isSelected && "border-primary bg-primary/5"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-foreground">
                          {pack.name}
                        </h3>
                        <span className="text-xs font-semibold text-muted-foreground">
                          {priceLabel}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {tokensNumeric} stars · about {videoEstimateLabel}
                      </p>
                      <p className="text-xs text-muted-foreground/80">
                        Each video runs about {MIN_DURATION_SECONDS}-
                        {MAX_DURATION_SECONDS} seconds.
                      </p>
                      {pack.description && (
                        <p className="mt-2 text-xs text-muted-foreground/80">
                          {pack.description}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-3 w-full justify-center"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleStartCheckout(pack.id);
                        }}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? "Redirecting…" : "Select pack"}
                      </Button>
                    </div>
                  );
                })}
                {tokenPacksLoading && (
                  <div className="sm:col-span-2 flex items-center justify-center gap-2 rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    Loading token packs…
                  </div>
                )}
                {!tokenPacksLoading && !tokenPacks.length && (
                  <div className="sm:col-span-2 rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                    Token packs are unavailable right now. Please try again soon.
                  </div>
                )}
              </div>
              {checkoutError && (
                <p className="text-sm text-destructive">{checkoutError}</p>
              )}
              {checkoutNotice && !checkoutError && (
                <p className="text-sm text-muted-foreground">{checkoutNotice}</p>
              )}
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setIsTokenDialogOpen(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Upload your doodle</CardTitle>
            <CardDescription>
              Snap a photo or upload any drawing — let&apos;s make it magical!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UploadDropzone
              accept={ACCEPTED_TYPES.join(",")}
              onFiles={handleFiles}
              disabled={isProcessing}
              className="min-h-[320px]"
            >
              {previewUrl ? (
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl border border-border/40 bg-white">
                  <img
                    src={previewUrl}
                    alt={file?.name || "Uploaded doodle preview"}
                    className="h-full w-full object-contain"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
                    Tap to change
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <UploadCloud className="h-7 w-7" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Drop your drawing here
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    PNG, JPG, or HEIC up to 10MB (auto-optimized). We&apos;ll keep the doodle safe
                    while the video is generated.
                  </p>
                </div>
              )}
            </UploadDropzone>
            {previewUrl && (
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  className="rounded-full border border-border/60 px-6 py-2 text-sm font-semibold text-foreground/80"
                  onClick={() => {
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                    }
                    setPreviewUrl("");
                    setFile(null);
                  }}
                >
                  Change Doodle
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Pick your magic theme</CardTitle>
              <CardDescription>
                Choose what kind of magic brings your doodle to life.
              </CardDescription>
            </div>
            <Dialog
              open={isThemeDialogOpen}
              onOpenChange={(open) => {
                setIsThemeDialogOpen(open);
                if (!open) {
                  setThemeQuery("");
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full px-4"
                  onClick={() => setThemeQuery("")}
                >
                  See all themes
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl space-y-6">
                <DialogHeader>
                  <DialogTitle>Select a magic theme</DialogTitle>
                  <DialogDescription>
                    Browse the full catalog or search for a specific vibe.
                  </DialogDescription>
                </DialogHeader>
                <Input
                  value={themeQuery}
                  onChange={(event) => setThemeQuery(event.target.value)}
                  placeholder="Search themes…"
                  className="h-10"
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  {themesLoading ? (
                    <div className="col-span-full flex items-center justify-center gap-2 rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      Loading themes…
                    </div>
                  ) : filteredThemes.length ? (
                    filteredThemes.map((theme) => (
                      <ThemeCard
                        key={theme.id}
                        title={theme.name}
                        description={theme.description}
                        emoji={theme.emoji}
                        tokens={theme.tokens}
                        active={selectedTheme === theme.id}
                        onClick={() => handleThemeSelect(theme.id, true)}
                        className="h-full"
                      />
                    ))
                  ) : (
                    <div className="col-span-full rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                      No themes match your search. Try a different keyword.
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setIsThemeDialogOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent className="space-y-4">
            {themesLoading ? (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  Loading themes…
                </div>
              </div>
            ) : featuredThemes.length ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {featuredThemes.map((theme) => (
                  <ThemeCard
                    key={theme.id}
                    title={theme.name}
                    description={theme.description}
                    emoji={theme.emoji}
                    tokens={theme.tokens}
                    active={selectedTheme === theme.id}
                    onClick={() => handleThemeSelect(theme.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                Themes will appear here.
              </div>
            )}
            {selectedThemeData && (
              <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <span className="text-base">{selectedThemeData.emoji}</span>
                  {selectedThemeData.name}
                </div>
                {selectedThemeData.description && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selectedThemeData.description}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Special requests (optional)</CardTitle>
            <CardDescription>
              Tell us what cool things should happen in the video!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              value={story}
              onChange={(event) => setStory(event.target.value)}
              rows={5}
              maxLength={500}
              placeholder="Example: Make the dragon breathe colorful fire! Add sparkly stars! Make it super silly!"
            />
            <p className="text-xs text-muted-foreground">
              Up to 500 characters. We&apos;ll pass the note to our narrator.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Video settings</CardTitle>
            <CardDescription>How long should the magic last?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="duration">How long?</Label>
              <Select
                value={String(durationSeconds)}
                onValueChange={(value) => setDurationSeconds(Number(value))}
              >
                <SelectTrigger id="duration">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option} seconds
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              This video costs ⭐ {durationSeconds}. You have ⭐{" "}
              {walletLoading ? "…" : Math.max(0, Number(tokenBalance))}.
            </div>
            <div className="space-y-3">
              <Button
                type="button"
                className="w-full rounded-full px-6 py-3 text-base font-semibold"
                onClick={handleGenerate}
                disabled={isProcessing}
              >
                <Wand2 className="mr-2 h-5 w-5" />
                {isProcessing ? "Making magic…" : "Create Video"}
              </Button>
              {queuedJobId && !downloadUrl && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
                  <div className="flex flex-col gap-1">
                    <span>Magic in progress! We&apos;ll send your video here once it finishes.</span>


                  </div>
                </div>
              )}
              {downloadUrl && (
                <div className="rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary">
                  <div className="flex items-center justify-between gap-3">
                    <span>Your video is ready!</span>
                    <Button asChild variant="secondary">
                      <a href={downloadUrl} download>
                        Download
                      </a>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;

